import React, { useRef, useLayoutEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

export default function ScienceMTC() {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const root = ref.current;
      if (!root) return;

      gsap.from(root.querySelectorAll(".sm-head > *"), {
        y: 18, autoAlpha: 0, duration: 0.55, ease: "power3.out", stagger: 0.08,
        scrollTrigger: { trigger: root, start: "top 85%", once: true }
      });

      gsap.from(root.querySelectorAll(".sm-card"), {
        y: 24, autoAlpha: 0, duration: 0.55, ease: "power3.out", stagger: 0.08,
        scrollTrigger: { trigger: ".sm-grid, .sm-copy", start: "top 82%", once: true }
      });

      gsap.from(root.querySelectorAll(".sm-ph"), {
        autoAlpha: 0, y: 20, scale: 0.96, rotate: (i) => (i % 2 ? 3 : -3),
        duration: 0.6, ease: "power3.out", stagger: 0.08,
        scrollTrigger: { trigger: ".sm-collage", start: "top 85%", once: true }
      });

      root.querySelectorAll(".sm-ph .sm-ill").forEach((img) => {
        gsap.to(img, {
          yPercent: -10, ease: "none",
          scrollTrigger: { trigger: img.closest(".sm-ph"), start: "top bottom", end: "bottom top", scrub: true }
        });
      });

      gsap.to(root.querySelector(".sm-hero .sm-shine"), {
        xPercent: 120, opacity: 1, duration: 1.2, ease: "power2.out",
        scrollTrigger: { trigger: ".sm-hero", start: "top 85%", once: true }
      });
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <section id="science-mtc" className="science-mtc" ref={ref} aria-labelledby="sm-title">
      <div className="sm-inner">
        <header className="sm-head">
          <p className="sm-kicker">Multiple Transportable Carbohydrates (MTCs)</p>
          <h2 id="sm-title" className="sm-title">Built on MTCs + high-DE maltodextrin</h2>
          <p className="sm-lede">
            Our blend is made from Maltodextrin, Glucose (dextrose), Fructose, Sucrose, Sea Salt, and Citric Acid.
            MTCs have shown a <strong>20–50% increase</strong> in exogenous carbohydrate oxidation vs single-carb
            sources and can <strong>reduce GI distress</strong>.
          </p>
        </header>

        {/* copy + hero */}
        <div className="sm-top">
          <div className="sm-copy">
            <article className="sm-card">
              <h3>Ingredients (straight up)</h3>
              <p>
                Maltodextrin (high DE), Glucose (dextrose), Fructose, Sucrose, Sea Salt, Citric Acid.
              </p>
            </article>

            <article className="sm-card">
              <h3>Why MTCs?</h3>
              <p>
                Taken together, these four carbohydrates (MTCs) have shown a 20–50% increase in exogenous
                carbohydrate oxidation vs single sources—and have also shown to decrease gastrointestinal
                distress compared with single-carb intake.
              </p>
            </article>

            <article className="sm-card">
              <h3>Not chasing 1:1 (or 1:0.8)</h3>
              <p>
                In my opinion, the 1:1 or 1:0.8 glucose:fructose research is non-conclusive and makes the formulation
                so sweet that easily consuming <strong>100 g/hr</strong> is literally breathtaking for many people
                (including myself). These ratios also ignore differences in maltodextrin <em>Dextrose Equivalent (DE)</em>.
              </p>
            </article>
          </div>

          <figure className="sm-hero">
            <img className="sm-ill" src="/DSCF9477.jpg" alt="High-solubility blend mixed pre-ride" loading="lazy" />
            <span className="sm-shine" aria-hidden />
          </figure>
        </div>

        {/* DE explanation */}
        <div className="sm-grid">
          <article className="sm-card">
            <h3>What DE means</h3>
            <p>
              Dextrose is glucose. Dextrose Equivalent (DE) is the degree of hydrolysis (breakdown) of starch.
              Maltodextrins vary: some are low DE, some high DE.
            </p>
          </article>

          <article className="sm-card">
            <h3>Our choice</h3>
            <p>
              We choose the <strong>highest DE maltodextrin</strong>, which behaves more like an MTC vs a single carbohydrate.
              Mixed with glucose/dextrose, fructose, and sucrose, we get a high glucose percentage with
              <strong> high solubility and low sweetness</strong>.
            </p>
          </article>

          <article className="sm-card">
            <h3>The result (why it’s easy)</h3>
            <p>
              That balance is the secret to getting calories in with minimal issues—less GI distress and a drink
              that’s easy to consume even on hot days.
            </p>
          </article>
        </div>

        {/* bottom line */}
        <div className="sm-grid">
          <article className="sm-card">
            <h3>Bottom line</h3>
            <p>
              We build on trusted research that’s been proven year after year. <strong>MTCs</strong> and
              <strong> 100 g/hr intake</strong> keep showing up in the data—and <strong>Dope Shot</strong> is the
              easiest way to get both.
            </p>
          </article>
        </div>

        {/* collage */}
        <div className="sm-collage" aria-label="Science in practice photos">
          <figure className="sm-ph p1">
            <img className="sm-ill" src="/DSCF9477.jpg" alt="Bench tests and glassware" loading="lazy" />
          </figure>
          <figure className="sm-ph p2">
            <img className="sm-ill" src="/DSCF9477.jpg" alt="Endurance ride—steady fueling" loading="lazy" />
          </figure>
          <figure className="sm-ph p3">
            <img className="sm-ill" src="/DSCF9477.jpg" alt="High solubility mix in a bottle" loading="lazy" />
          </figure>
        </div>
      </div>
    </section>
  );
}
